import React, { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Context } from "../../context/Context";
import { useAccess } from "../hooks/useAccess";
import { canRoute, homeRouteFor, resolveRoutePolicy } from "../core/routePolicies";
import { REASONS } from "../core/policy";
import BranchAssignmentRequired from "./BranchAssignmentRequired";

/**
 * Route-level guard.
 *
 *   <RouteGuard permission="menu.view"><Menu /></RouteGuard>
 *
 * Without `permission` the guard looks the current URL up in routePolicies.
 * While access is still loading nothing is rendered (no flash of protected
 * UI). When denied the user goes to `redirectTo`, or to their own landing
 * route - never to a route they cannot open. A branch user without an assigned
 * branch sees an explanation instead of a redirect (NA-035).
 */
export function RouteGuard({
  permission,
  alsoPermission,
  resource,
  redirectTo,
  loading = null,
  children,
}) {
  const { can, explain, isAccessReady, isAuthenticated } = useAccess();
  const { currentUserInfo } = useContext(Context) || {};
  const location = useLocation();

  if (!isAuthenticated) return <Navigate to={redirectTo || "/"} replace />;
  if (!isAccessReady) return loading;

  const matched = permission ? null : resolveRoutePolicy(location.pathname);
  const required = permission ?? matched?.permission;
  const scope = resource ?? matched?.resource;
  const alternative = permission ? alsoPermission : matched?.or;
  if (required && canRoute(can, { permission: required, or: alternative }, scope)) return <>{children}</>;
  // A branch user without an assigned branch is told why, instead of being
  // redirected or shown an empty page (NA-035).
  if (required && explain(required, scope).reason === REASONS.BRANCH_ASSIGNMENT_REQUIRED) {
    return <BranchAssignmentRequired />;
  }

  const target = redirectTo || homeRouteFor(currentUserInfo) || "/";
  return <Navigate to={target === location.pathname ? "/" : target} replace />;
}

export default RouteGuard;
